<template>
  <div class="intercom-analytics">
    <div class="analytics-header">
      <div class="flex items-center justify-between">
        <span class="text-sm text-gray-600">Messaging data from Intercom</span>
        <button
          v-if="!loading && analytics"
          :disabled="syncing"
          class="sync-button"
          @click="syncData">
          <i
            v-if="syncing"
            class="fas fa-spinner fa-spin mr-1.5" />
          <i
            v-else
            class="fas fa-sync mr-1.5" />
          {{ syncing ? 'Syncing...' : 'Sync' }}
        </button>
      </div>
    </div>

    <div
      v-if="loading"
      class="content-body">
      <div class="flex items-center justify-center py-8">
        <div class="text-center">
          <i class="fas fa-spinner fa-spin text-3xl text-indigo-600 mb-2" />
          <p class="text-sm text-gray-500">
            Loading analytics...
          </p>
        </div>
      </div>
    </div>

    <div
      v-else-if="error"
      class="content-body">
      <div class="error-state">
        <i class="fas fa-exclamation-triangle error-state-icon" />
        <p class="error-state-text">
          {{ error }}
        </p>
        <button
          class="action-button action-button-primary mt-4"
          @click="loadData">
          <i class="fas fa-redo mr-1.5" />
          Try Again
        </button>
      </div>
    </div>

    <div
      v-else-if="analytics"
      class="content-body">
      <!-- Summary Cards -->
      <div class="summary-grid">
        <div class="summary-card summary-card-blue">
          <div class="summary-label">
            <i class="fas fa-comments mr-1.5" />
            Total Conversations
          </div>
          <div class="summary-value">
            {{ analytics.summary.totalConversations }}
          </div>
        </div>
        <div class="summary-card summary-card-green">
          <div class="summary-label">
            <i class="fas fa-check-circle mr-1.5" />
            Closed
          </div>
          <div class="summary-value">
            {{ analytics.summary.closedConversations }}
          </div>
        </div>
        <div class="summary-card summary-card-yellow">
          <div class="summary-label">
            <i class="fas fa-clock mr-1.5" />
            Open
          </div>
          <div class="summary-value">
            {{ analytics.summary.openConversations }}
          </div>
        </div>
        <div class="summary-card summary-card-purple">
          <div class="summary-label">
            <i class="fas fa-star mr-1.5" />
            Avg Rating
          </div>
          <div class="summary-value">
            {{ analytics.summary.averageRating ? analytics.summary.averageRating.toFixed(1) : 'N/A' }}
          </div>
        </div>
      </div>

      <!-- Time Period Stats -->
      <div class="trends-section">
        <h4 class="section-title">
          Conversation Trends
        </h4>
        <div class="trends-grid">
          <div
            v-for="(stats, period) in analytics.timePeriods"
            :key="period"
            class="trend-card">
            <div class="trend-label">
              {{ formatPeriodLabel(period) }}
            </div>
            <div class="trend-value">
              {{ stats.total }}
            </div>
            <div class="trend-details">
              <span
                v-if="stats.averageResponseTime"
                class="trend-detail">
                <i class="fas fa-reply text-gray-400 mr-1" />
                {{ stats.averageResponseTime.formatted }}
              </span>
              <span
                v-if="stats.averageRating"
                class="trend-detail">
                <i class="fas fa-star text-yellow-500 mr-1" />
                {{ stats.averageRating.toFixed(1) }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Recent Conversations -->
      <div
        v-if="analytics.recentConversations && analytics.recentConversations.length > 0"
        class="conversations-section">
        <div class="conversations-header">
          <h4 class="section-title mb-0">
            Recent Conversations
          </h4>
          <button
            v-if="analytics.recentConversations.length > 5 && !showAllConversations"
            class="view-all-button"
            @click="toggleConversations(true)">
            View All ({{ analytics.recentConversations.length }})
          </button>
          <button
            v-else-if="showAllConversations"
            class="view-all-button"
            @click="toggleConversations(false)">
            Show Less
          </button>
        </div>
        <div class="conversations-list">
          <div
            v-for="conversation in displayedConversations"
            :key="conversation.id"
            class="conversation-item">
            <div class="flex items-start justify-between">
              <div class="flex-1">
                <div class="conversation-info">
                  <span class="conversation-contact">{{ conversation.contact.name || conversation.contact.email }}</span>
                  <span
                    :class="getStatusBadgeClass(conversation.status)"
                    class="status-badge">
                    {{ conversation.status }}
                  </span>
                </div>
                <div
                  v-if="conversation.lastMessage"
                  class="conversation-message"
                  v-html="stripHtml(conversation.lastMessage.body)" />
                <div class="conversation-meta">
                  <span class="meta-item">
                    <i class="fas fa-clock mr-1" />
                    {{ formatDate(conversation.createdAt) }}
                  </span>
                  <span
                    v-if="conversation.rating"
                    class="meta-item">
                    <i class="fas fa-star mr-1 text-yellow-500" />
                    {{ conversation.rating }}
                  </span>
                  <span
                    v-if="conversation.tags.length > 0"
                    class="meta-item">
                    <i class="fas fa-tags mr-1" />
                    {{ conversation.tags.join(', ') }}
                  </span>
                </div>
              </div>
              <a
                :href="`https://app.intercom.com/a/inbox/${intercomAppId}/inbox/conversation/${conversation.intercomId}`"
                target="_blank"
                class="conversation-link">
                <i class="fas fa-external-link-alt text-xs" />
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- Tag Distribution -->
      <div
        v-if="analytics.tags && Object.keys(analytics.tags).length > 0"
        class="tags-section">
        <h4 class="section-title">
          Popular Tags
        </h4>
        <div class="tags-list">
          <span
            v-for="(count, tag) in analytics.tags"
            :key="tag"
            class="tag-item">
            {{ tag }}
            <span class="tag-count">({{ count }})</span>
          </span>
        </div>
      </div>
    </div>

    <div
      v-else
      class="content-body">
      <div class="empty-state">
        <i class="fas fa-comments empty-state-icon" />
        <p class="empty-state-text">
          No Intercom data available
        </p>
        <button
          class="action-button action-button-primary mt-4"
          @click="syncData">
          <i class="fas fa-sync mr-1.5" />
          Sync Intercom Data
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import mdtoast from '@dmuy/toast'
import axios from '../init/axios'

export default {
  name: 'IntercomAnalytics',
  props: {
    organizationId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      loading: false,
      syncing: false,
      error: null,
      analytics: null,
      intercomAppId: 'x3govr5q', // From the _intercom.html.erb partial
      showAllConversations: false
    }
  },
  computed: {
    displayedConversations() {
      if (!this.analytics || !this.analytics.recentConversations) {
        return []
      }
      return this.showAllConversations ?
        this.analytics.recentConversations :
        this.analytics.recentConversations.slice(0, 5)
    }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      this.error = null

      try {
        const response = await axios.get(`/ajax/organizations/${this.organizationId}/intercom_analytics`)
        if (response.data.success) {
          this.analytics = response.data.data
        } else {
          this.error = 'Failed to load analytics data'
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading Intercom analytics:', error)
        this.error = 'Failed to load analytics data. Please try again.'
      } finally {
        this.loading = false
      }
    },

    async syncData() {
      this.syncing = true
      this.error = null

      try {
        const response = await axios.post(`/ajax/organizations/${this.organizationId}/intercom_analytics/sync`)
        if (response.data.success) {
          // Show success message
          mdtoast.success('Intercom sync initiated successfully', { duration: 3000 })
          // Reload data after a short delay to allow sync to process
          setTimeout(() => {
            this.loadData()
          }, 2000)
        } else {
          this.error = response.data.error || 'Sync failed'
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error syncing Intercom data:', error)
        this.error = 'Failed to sync data. Please try again.'
      } finally {
        this.syncing = false
      }
    },

    formatPeriodLabel(period) {
      const labels = {
        last30Days: 'Last 30 Days',
        last90Days: 'Last 90 Days',
        last180Days: 'Last 180 Days',
        allTime: 'All Time'
      }
      return labels[period] || period
    },

    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      const now = new Date()
      const diffTime = Math.abs(now - date)
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))

      if (diffDays === 0) {
        const diffHours = Math.floor(diffTime / (1000 * 60 * 60))
        if (diffHours === 0) {
          const diffMinutes = Math.floor(diffTime / (1000 * 60))
          return `${diffMinutes}m ago`
        }
        return `${diffHours}h ago`
      } if (diffDays < 7) {
        return `${diffDays}d ago`
      }
      return date.toLocaleDateString()
    },

    getStatusBadgeClass(status) {
      switch (status) {
        case 'open':
          return 'bg-yellow-100 text-yellow-700'
        case 'closed':
          return 'bg-green-100 text-green-700'
        case 'snoozed':
          return 'bg-gray-100 text-gray-700'
        default:
          return 'bg-gray-100 text-gray-700'
      }
    },

    stripHtml(html) {
      if (!html) return ''
      // Remove HTML tags and decode entities
      const tmp = document.createElement('div')
      tmp.innerHTML = html
      return tmp.textContent || tmp.innerText || ''
    },

    toggleConversations(show) {
      this.showAllConversations = show
      // Wait for DOM update then trigger resize
      this.$nextTick(() => {
        // Dispatch a custom event that the parent can listen to
        window.dispatchEvent(new CustomEvent('intercom-content-changed'))
      })
    }
  }
}
</script>

<style scoped>
.intercom-analytics {
  /* Remove card styling - will be wrapped in CollapsibleCard */
}

.analytics-header {
  @apply mb-4;
}

.sync-button {
  @apply inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md
         bg-indigo-600 text-white hover:bg-indigo-700
         transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500
         disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-indigo-600;
}

.content-body {
  /* Content body without padding since CollapsibleCard provides it */
}

/* Summary Cards */
.summary-grid {
  @apply grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6;
}

.summary-card {
  @apply rounded-lg p-4 space-y-1;
}

.summary-card-blue {
  @apply bg-blue-50 border border-blue-100;
}

.summary-card-green {
  @apply bg-green-50 border border-green-100;
}

.summary-card-yellow {
  @apply bg-yellow-50 border border-yellow-100;
}

.summary-card-purple {
  @apply bg-purple-50 border border-purple-100;
}

.summary-label {
  @apply text-xs font-semibold uppercase tracking-wider flex items-center;
}

.summary-card-blue .summary-label {
  @apply text-blue-600;
}

.summary-card-green .summary-label {
  @apply text-green-600;
}

.summary-card-yellow .summary-label {
  @apply text-yellow-600;
}

.summary-card-purple .summary-label {
  @apply text-purple-600;
}

.summary-value {
  @apply text-2xl font-bold;
}

.summary-card-blue .summary-value {
  @apply text-blue-900;
}

.summary-card-green .summary-value {
  @apply text-green-900;
}

.summary-card-yellow .summary-value {
  @apply text-yellow-900;
}

.summary-card-purple .summary-value {
  @apply text-purple-900;
}

/* Trends Section */
.trends-section {
  @apply mb-6;
}

.section-title {
  @apply text-xs font-semibold text-gray-600 uppercase tracking-wider mb-3 flex items-center;
}

.trends-grid {
  @apply grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4;
}

.trend-card {
  @apply bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-1;
}

.trend-label {
  @apply text-xs text-gray-500 font-medium;
}

.trend-value {
  @apply text-lg font-semibold text-gray-900;
}

.trend-details {
  @apply space-y-0.5 mt-2;
}

.trend-detail {
  @apply text-xs text-gray-600 flex items-center;
}

/* Conversations Section */
.conversations-section {
  @apply mb-6;
}

.conversations-header {
  @apply flex items-center justify-between mb-3;
}

.conversations-list {
  @apply space-y-2;
}

.view-all-button {
  @apply text-xs text-indigo-600 hover:text-indigo-700 font-medium transition-colors;
}

.conversation-item {
  @apply border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors;
}

.conversation-info {
  @apply flex items-center justify-between mb-1;
}

.conversation-contact {
  @apply text-sm font-medium text-gray-900;
}

.conversation-message {
  @apply text-sm text-gray-600 line-clamp-2;
}

.conversation-meta {
  @apply flex items-center gap-4 mt-2 text-xs text-gray-500;
}

.meta-item {
  @apply inline-flex items-center;
}

.conversation-link {
  @apply text-indigo-600 hover:text-indigo-800 transition-colors p-1;
}

/* Status Badge */
.status-badge {
  @apply text-xs px-2 py-0.5 rounded-full font-medium;
}

/* Tags Section */
.tags-section {
  @apply mb-6;
}

.tags-list {
  @apply flex flex-wrap gap-2;
}

.tag-item {
  @apply inline-flex items-center px-3 py-1 rounded-full text-xs font-medium
         bg-gray-100 text-gray-700 border border-gray-200;
}

.tag-count {
  @apply ml-1 text-gray-500;
}

/* Empty & Error States */
.empty-state {
  @apply text-center py-8;
}

.empty-state-icon {
  @apply text-4xl text-gray-300 mb-3;
}

.empty-state-text {
  @apply text-sm text-gray-500;
}

.error-state {
  @apply text-center py-8;
}

.error-state-icon {
  @apply text-4xl text-red-300 mb-3;
}

.error-state-text {
  @apply text-sm text-red-600 mb-4;
}

/* Action Buttons */
.action-button {
  @apply inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg
         transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2
         disabled:opacity-50 disabled:cursor-not-allowed;
}

.action-button-primary {
  @apply bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-indigo-500;
}
</style>
